// React and related imports
import React, { useEffect } from 'react';

// Custom components
import { TimePicker } from './TimePicker';
import { SelectPrayersName } from './SelectPrayersName';
import { TimeDifferenceInput } from './TimeDifferenceInput';
import { SelectionModeRadioButtons } from './SelectionModeRadioButtons';

// i18 Translation
import { TFunction } from 'i18next';

// Types and Interfaces
import { PrayersName } from '../../../common/enums/PrayersName';
import { ContextState } from '../../../hooks/useContentContextState';

// Custom Hooks
import { usePrayerTimeSettings } from '../../../hooks/usePrayerTimeSettings';

interface PrayerTimeSettingsProps {
  t: TFunction<'translation', undefined>;
  contextState: ContextState;
  updateField: (field: keyof ContextState, value: string) => void;
  validateField: (field: string, value: string) => void;
}

export const PrayerTimeSettings: React.FC<PrayerTimeSettingsProps> = ({
  t,
  contextState,
  updateField,
  validateField,
}) => {
  const { selectionMode, handleSelectionChange, handleFieldUpdate } =
    usePrayerTimeSettings({
      initialMode: 'time',
      contextState,
      validateField,
      updateField,
    });

  useEffect(() => {
    if (contextState?.time) {
      handleSelectionChange('time');
    }

    if (contextState?.prayerName) {
      handleSelectionChange('prayer');
    }
  }, [contextState?.time, contextState?.prayerName]);

  return (
    <div className="space-y-4">
      <SelectionModeRadioButtons
        selectionMode={selectionMode}
        handleSelectionChange={handleSelectionChange}
        t={t}
      />

      {selectionMode === 'time' && (
        <TimePicker
          id="time"
          label={t('contentContext.time')}
          value={contextState?.time || ''}
          onChange={(value) =>
            handleFieldUpdate('time' as keyof ContextState, value)
          }
        />
      )}

      {selectionMode === 'prayer' && (
        <>
          <SelectPrayersName
            id="prayerName"
            label={t('contentContext.prayerName')}
            placeholder={t('contentContext.prayerName')}
            value={contextState?.prayerName || ''}
            options={Object.values(PrayersName)}
            onChange={(value) =>
              handleFieldUpdate('prayerName' as keyof ContextState, value)
            }
          />

          <TimeDifferenceInput
            id="prayerDelta"
            label={t('contentContext.prayerDelta')}
            value={contextState?.adhanDelta || ''}
            onChange={(value) =>
              handleFieldUpdate('adhanDelta' as keyof ContextState, value)
            }
          />
        </>
      )}
    </div>
  );
};
